import { createAxios } from '../auth'
import { IRIS_BASE_URL } from '.'
import { UUID } from '../misc/domain'

// Response DTOs — snake_case to match iris-server's Jackson serialization.

export interface CallParticipant {
  user_id: UUID
  name?: string
  is_interpreter: boolean
}

export interface CallSummary {
  id: UUID
  status: string
  room_type: string
  is_emergency: boolean
  created_date: string
  in_call_date?: string
  end_date?: string
  duration_seconds?: number
  participants: CallParticipant[]
  has_recording: boolean
}

export interface PagedCalls {
  items: CallSummary[]
  total: number
  offset: number
  limit: number
}

export interface RecordingSide {
  publisher_id: UUID
  name?: string
  is_interpreter: boolean
  has_audio: boolean
  has_video: boolean
}

export interface CallRecordings {
  room_id: UUID
  sides: RecordingSide[]
  composite?: string
}

export interface CallListFilter {
  from?: string
  to?: string
  status?: string
  offset?: number
  limit?: number
}

const ADMIN_BASE = `${IRIS_BASE_URL}/admin`

export async function fetchCalls(token: string, filter: CallListFilter = {}): Promise<PagedCalls> {
  const params = new URLSearchParams()
  if (filter.from) params.set('from', filter.from)
  if (filter.to) params.set('to', filter.to)
  if (filter.status) params.set('status', filter.status)
  params.set('offset', String(filter.offset ?? 0))
  params.set('limit', String(filter.limit ?? 50))
  const resp = await createAxios(token).get<PagedCalls>(`${ADMIN_BASE}/calls?${params.toString()}`)
  return resp.data
}

/** Fetch recording detail for a call. Also triggers on-demand composite conversion server-side. */
export async function fetchRecordings(token: string, roomId: UUID): Promise<CallRecordings> {
  const resp = await createAxios(token).get<CallRecordings>(`${ADMIN_BASE}/calls/${roomId}/recordings`)
  return resp.data
}

export type CompositeResult =
  | { status: 'ready'; url: string }
  | { status: 'building' }

/**
 * Fetch the composited side-by-side video. Media elements can't attach the Bearer header, so we
 * pull it as an authorized blob and hand back an object URL. Returns 'building' while the server
 * is still converting (HTTP 202); callers should retry.
 */
export async function fetchComposite(token: string, roomId: UUID): Promise<CompositeResult> {
  const resp = await createAxios(token).get(`${ADMIN_BASE}/calls/${roomId}/composite.webm`, {
    responseType: 'blob',
    validateStatus: s => s === 200 || s === 202
  })
  if (resp.status === 200) {
    return { status: 'ready', url: URL.createObjectURL(resp.data as Blob) }
  }
  return { status: 'building' }
}
